import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { isSessionMultipleTiers } from 'utils';
import PeopleIcon from 'assets/icon-user';
import OpenMenuIcon from 'assets/chevron-down';
import styled from 'styled-components';
import { SkeletonLine } from 'components/theme/Skeleton/Skeleton';
import useSearchQueryParams from 'hooks/use-search-params';
import SelectWrapper from './SelectWrapper';
import ErrorMessage from './ErrorMessage';
import { WrapperFirst, ParticipantWrapper } from './styles';

interface WrapperSecondProps {
  isDropdownVisible: boolean;
}
export const WrapperSecond = styled.div<WrapperSecondProps>`
  display: flex;
  align-items: center;
  max-height: fit-content;
  & > div {
    height: 18px;
    border-left: 0.5px solid var(--way-palette-black-70);
    margin-right: 5px;
  }

  & > svg {
    transform: scale(0.8);
    stroke: ${({ isDropdownVisible }) =>
      isDropdownVisible ? 'darkgray' : 'lightgray'};
  }
`;

interface PriceTier {
  name: string;
  price: number;
  id: string;
}

interface Option {
  value: number;
  label: number;
}

interface ParticipantsSelectorProps {
  availablePriceTiers: PriceTier[];
  availableSeatsForSession: Option[];
  control: Control;
  errors: FieldErrors;
  isDropdownVisible: boolean;
  isFetchingSessionTiers: boolean;
  setIsDropdownVisible: (isVisible: boolean) => void;
  setSinglePriceTierCount: (count: number) => void;
  totalSelectedParticipants: number;
  clearErrors: (name?: string) => void;
}

const ParticipantsSelector = ({
  availablePriceTiers,
  availableSeatsForSession,
  control,
  errors,
  isDropdownVisible,
  isFetchingSessionTiers,
  setIsDropdownVisible,
  setSinglePriceTierCount,
  totalSelectedParticipants,
  clearErrors,
}: ParticipantsSelectorProps) => {
  const { t: translate } = useTranslation();
  const { searchParams } = useSearchQueryParams();
  const participantsSelectId = 'participants-select';

  if (isFetchingSessionTiers) {
    return (
      <SkeletonLine
        style={{
          height: '54px',
          width: '173px',
          minWidth: '173px',
          marginBottom: '0px',
        }}
      />
    );
  }

  return (
    <>
      {isSessionMultipleTiers(availablePriceTiers) ? (
        <ParticipantWrapper
          id={participantsSelectId}
          className="experience-view__participant-wrapper"
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={isDropdownVisible}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsDropdownVisible(true);
            }
          }}
          onClick={(e) => {
            setIsDropdownVisible(true);
            e.stopPropagation();
          }}
          aria-label={translate('selectParticipants')}
          aria-describedby={
            errors?.selectParticipantValidation
              ? 'selectParticipantValidation-error'
              : undefined
          }
        >
          <WrapperFirst className="experience-view__first-participant-wrapper">
            <PeopleIcon />
            <span>{totalSelectedParticipants || translate('select')}</span>
          </WrapperFirst>
          <WrapperSecond isDropdownVisible={isDropdownVisible}>
            <div />
            <OpenMenuIcon />
          </WrapperSecond>
        </ParticipantWrapper>
      ) : (
        <Controller
          control={control}
          name="noOfGuests"
          rules={{ required: true }}
          defaultValue={
            searchParams?.noOfGuests
              ? {
                  label: searchParams.noOfGuests,
                  value: searchParams.noOfGuests,
                }
              : { label: 1, value: 1 }
          }
          render={({ field }) => (
            <SelectWrapper
              options={availableSeatsForSession}
              value={field.value}
              onChange={(event) => {
                const selected = event.target.value;
                setSinglePriceTierCount(Number(selected));
                field.onChange(selected);
                clearErrors('noOfGuests');
              }}
              aria-describedby={
                errors?.noOfGuests ? 'noOfGuests-error' : undefined
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          )}
        />
      )}
      <ErrorMessage
        errors={errors}
        name="noOfGuests"
        message="No. of Guests is Required"
      />
    </>
  );
};

export default ParticipantsSelector;
